import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { take, filter } from 'rxjs/operators';

import { MonitoringSourcePickerFieldModel } from '../../../Shared/models/monitoring-source-picker-field.model';
import { TextFieldModel } from '../../../../Common/PageBuilder/models/text-field.model';
import { MonitoringBackEndDataSourceModel } from '../../../Shared/models/monitoring-back-end-data-source.model';
import { MonitoringSeriesTypeEnum } from '../../../Shared/enums/monitoring-series-type.enum';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { BriefingsService } from '../../services/briefings.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { MentionSearchService } from '../../../Core/services/mention-search.service';
import { monitoringFiltersEnum } from '../../../Shared/enums/monitoring-filters.enum';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { briefingUnsavedSourceOrganizeOptions } from '../../models/briefing-organize-by-options.model';
import { BriefingOrganizeByEnum } from '../../enums/briefing-organize-by.enum';
import { BriefingSortByEnum } from '../../enums/briefing-sort-by.enum';
import { getAutomaticBlockSortOptions } from '../../models/briefing-sort-by-options.model';
import { BriefingConfigService } from '../../../../Common/MonitorShared/services/briefing-config.service';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { CachedDateService } from '@Common/MonitorShared/services/cached-date.service';

@Component({
    selector: 'agpr-automatic-coverage-dialog',
    templateUrl: './automatic-coverage-dialog.component.html',
    styleUrls: ['./automatic-coverage-dialog.component.scss'],
    standalone: false
})
export class AutomaticCoverageDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    readonly id = 'automaticCoverageDialog';
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly maxSectionNameLength = 256;

    cachedDateQuery: DateQueryModel;

    organizeOptions = briefingUnsavedSourceOrganizeOptions;
    organizeByDropdown: DropdownOptionModel = new DropdownOptionModel();
    organizeBy: BriefingOrganizeByEnum;
    sortOptions = getAutomaticBlockSortOptions();

    sortBy: BriefingSortByEnum = null;
    sortByDropdown: DropdownOptionModel = new DropdownOptionModel();

    dataSource: MonitoringBackEndDataSourceModel = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.allSavedCoverage);
    sourceField: MonitoringSourcePickerFieldModel = new MonitoringSourcePickerFieldModel(
        null,
        'backendDataSource',
        '',
        '',
        {
            dataSourceLabel: 'Monitoring.sourcePickerField.coverageSource',
            dateRangeLabel: 'Monitoring.sourcePickerField.dateRange',
            mentionCountLocalizationLabel: 'Monitoring.sourcePickerField.briefingsResultFeedbackMessage',
            maxNumberOfMentions: 1000,
            dataSourceNullErrorMessage: 'Monitoring.sourcePickerField.dataSourceRequiredMessage',
            dataSourceOptions: [
                new DropdownOptionModel('Briefings.allSavedCoverage', MonitoringSeriesTypeEnum.allSavedCoverage),
                new DropdownOptionModel('Reports.series.type.topic', MonitoringSeriesTypeEnum.topic),
                new DropdownOptionModel('Reports.series.type.folder', MonitoringSeriesTypeEnum.folder),
                new DropdownOptionModel('Reports.series.type.tag', MonitoringSeriesTypeEnum.tag),
                new DropdownOptionModel('Reports.series.type.tagGroup', MonitoringSeriesTypeEnum.tagGroup),
                new DropdownOptionModel('Reports.series.type.subscription', MonitoringSeriesTypeEnum.subscription),
                new DropdownOptionModel('Monitoring.source.type.sourceGroup', MonitoringSeriesTypeEnum.sourceGroup),
                new DropdownOptionModel('Reports.series.type.socialTopic', MonitoringSeriesTypeEnum.socialTopic),
                new DropdownOptionModel('Reports.series.type.theme', MonitoringSeriesTypeEnum.theme),
                new DropdownOptionModel('Reports.series.type.aiAgent', MonitoringSeriesTypeEnum.aiAgent)
            ],
            showResultsFeedback: false,
            showLoadingFeedback: false,
            showTooManyMentionsError: false,
            showFilters: true,
            orderedAccountFilters: MentionSearchService.defaultOrderedAccountFilters.concat(monitoringFiltersEnum.regions),
            orderedMercuryFilters: MentionSearchService.defaultOrderedMercuryFilters.concat(monitoringFiltersEnum.regions)
        }
    );

    name: string;
    emptyNameError: boolean = false;
    nameTooLongError: boolean = false;

    nameField = new TextFieldModel(
        null,
        'title',
        'Briefings.mentionList.form.newSectionName',
        null,
        false,
        this.maxSectionNameLength
    );

    showErrors: boolean = false;

    maxNumberOfMentions: number = 10;
    minValue: number = 1;
    maxValue: number = 1000;
    maxMentionsError: boolean = false;

    private subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        public briefingsService: BriefingsService,
        private briefingConfigService: BriefingConfigService,
        private cachedDateService: CachedDateService
    ) {
        super(coreDialogService, 'AutomaticCoverage');

        this.organizeByDropdown = this.organizeOptions[0];
        this.organizeBy = this.organizeByDropdown.value;

        this.sortByDropdown = this.sortOptions[0];
        this.sortBy = this.sortOptions[0].value;
    }

    ngOnInit() {
        this.subscriptions.push(
            this.briefingConfigService.getConfig().pipe(filter(x => !!x)).subscribe(config => {
                if (config.hasEngagementUpdates) {
                    this.sortOptions.push({
                        label: 'Briefings.input.sortMentionsBy.engagementMost',
                        value: BriefingSortByEnum.ENGAGEMENT_MOST
                    });
                }
            }),
            this.cachedDateService.getCachedSelectedDate().subscribe(
                cachedDate => {
                    this.cachedDateQuery = cachedDate;
                    if (this.cachedDateQuery) {
                        if (this.dataSource?.combinedSearchCriteria?.query) {
                            this.dataSource.combinedSearchCriteria.query.dateQuery = this.cachedDateQuery;
                        }
                        if (this.dataSource?.combinedSearchCriteria?.filterQuery) {
                            this.dataSource.combinedSearchCriteria.filterQuery.dateQuery = this.cachedDateQuery;
                        }
                        if (this.dataSource?.filterSearchCriteria?.query) {
                            this.dataSource.filterSearchCriteria.query.dateQuery = this.cachedDateQuery;
                        }
                    }
                }
            ),
        );
    }

    validateSource(): boolean {
        let isValid = true;

        if (!this.dataSource || (!this.dataSource.id && this.dataSource.type !== MonitoringSeriesTypeEnum.allSavedCoverage)) {
            isValid =  false;
        }

        this.sourceField.showDataSourceError = !isValid;

        return isValid;
    }
    validateName(): boolean {
        this.emptyNameError = false;
        this.nameTooLongError = false;

        if (!this.name) {
            this.emptyNameError = true;

        } else if (this.name.length === this.maxSectionNameLength) {
            this.nameTooLongError = true;
        }

        return !(this.emptyNameError || this.nameTooLongError);
    }

    validateMaxMentions(): boolean {
        let isValid = true;

        if (!this.maxNumberOfMentions) {
            isValid =  false;
        }

        this.maxMentionsError = !isValid;

        return isValid;
    }

    isValid(): boolean {
        let valid = true;

        valid = this.validateSource() && valid;
        valid = this.validateName() && valid;
        valid = this.validateMaxMentions() && valid;

        return valid;
    }

    addCoverage(): void {
        this.showErrors = true;

        if (!this.isValid()) {
            return;
        } else {
            this.briefingsService.addDynamicBlock(this.dataSource, this.maxNumberOfMentions, this.name, this.organizeBy, this.sortBy);
            this.closeDialog();
        }

        this.briefingsService.checkMaxMentionsExceeded().pipe(
            take(1),
            filter(x => !!x)
        ).subscribe(() => {
            this.briefingsService.showMentionLimitReachedDialog();
        });
    }

    cancel(): void {
        this.closeDialog();
    }

    sourceSelected(dataSource: MonitoringBackEndDataSourceModel): void {
        this.dataSource = dataSource;

        if (this.showErrors) {
            this.validateSource();
        }

    }

    nameChange(name: string): void {
        this.name = name;
        if (this.showErrors) {
            this.validateName();
        }
    }

    maxMentionsInTopicChanged(mentions: number): void {
        this.maxNumberOfMentions = mentions;

        if (mentions > this.maxValue) {
            this.maxNumberOfMentions = this.maxValue;
        }
        if (mentions < this.minValue) {
            this.maxNumberOfMentions = this.minValue;
        }
        if (this.showErrors) {
            this.validateMaxMentions();
        }
    }

    onOrganizeByChanged(organizeBy: BriefingOrganizeByEnum): void {
        this.organizeBy = organizeBy;
    }

    onSortByChanged(sortBy: BriefingSortByEnum): void {
        this.sortBy = sortBy;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
